import { createContext, useContext, useMemo, useRef, type ReactNode, type JSX } from 'react'
import { Toast } from 'primereact/toast'

// §0.A aria-live verification:
// PrimeReact Toast (v10.x) natively emits role="alert" aria-live="assertive"
// aria-atomic="true" on each toast message element (toast.esm.js line 357-359).
// No wrapping in a separate aria-live region is needed.

interface ToastApi {
  success: (msg: string, detail?: string) => void
  error: (msg: string, detail?: string) => void
  info: (msg: string, detail?: string) => void
  warning: (msg: string, detail?: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const toastRef = useRef<Toast>(null)
  const api = useMemo<ToastApi>(
    () => ({
      success: (s, d) =>
        toastRef.current?.show({ severity: 'success', summary: s, detail: d, life: 4000 }),
      error: (s, d) =>
        toastRef.current?.show({ severity: 'error', summary: s, detail: d, life: 8000 }),
      info: (s, d) =>
        toastRef.current?.show({ severity: 'info', summary: s, detail: d, life: 4000 }),
      warning: (s, d) =>
        toastRef.current?.show({ severity: 'warn', summary: s, detail: d, life: 6000 }),
    }),
    [],
  )
  return (
    <ToastContext.Provider value={api}>
      {/* PrimeReact Toast natively provides role="alert" aria-live="assertive" on each message.
          Stacking is NOT set here: PrimeReact writes an inline z-index (its `toast` base) on
          this element whenever a message shows, so a class would never win. The base lives in
          PrimeReact.zIndex in main.tsx — see the OVERLAY LAYER SCALE block in
          styles/primereact-overrides.css. */}
      <Toast ref={toastRef} position="top-right" />
      {children}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside ToastProvider')
  return ctx
}
